import {
  ApolloClient,
  FetchPolicy,
  useApolloClient,
  useLazyQuery as useApolloLazyQuery,
  useQuery,
  WatchQueryFetchPolicy,
} from "@apollo/client";
import { isLoading } from "apollo/utils";
import {
  ACTIVATE_PROVIDER_SEARCH_ACCOUNT,
  ADMISSIONS_LOGIN_WIZARD,
  GET_ACCOUNT,
  GET_ACCOUNTS,
  GET_ACCOUNTS_FOR_CAREPROVIDER,
  GET_ACCOUNTS_FOR_CARESEEKER,
  GET_ADDRESS_FROM_ZIPCODE,
  GET_ADMISSIONS_AUCTION,
  GET_ASSESSMENT_FORM_FIELDS,
  GET_AUCTION,
  GET_AUCTION_FORMS,
  GET_AUCTION_WITH_WINNER_CONTACTS,
  GET_AUCTIONREQUEST,
  GET_AVAILABLE_SEARCH_TYPES,
  GET_BEDROCK_PLAYGROUND,
  GET_CAREPROVIDER,
  GET_CAREPROVIDER_ADMISSIONS_PAGE,
  GET_CAREPROVIDER_DPA,
  GET_CAREPROVIDER_FILES,
  GET_CAREPROVIDER_INFO,
  GET_CAREPROVIDER_NOTIFICATIONS,
  GET_CAREPROVIDER_PATIENT_REQUEST,
  GET_CAREPROVIDER_REQUESTS_COUNT,
  GET_CAREPROVIDER_REQUESTS_NEW,
  GET_CAREPROVIDER_REVERSE_SEARCHES,
  GET_CAREPROVIDER_TOKEN,
  GET_CAREPROVIDER_WARNINGS,
  GET_CARESEEKER,
  GET_CARESEEKER_ACCOUNTS,
  GET_CARESEEKER_PATIENTS_COUNT,
  GET_CHAT,
  GET_CHATS,
  GET_CLINICAL_CONSULTATIONS,
  GET_CLINICAL_PATIENTS,
  GET_COMPANY,
  GET_COMPANY_TOKEN,
  GET_CONSULTANT,
  GET_CONSULTANT_EVENTS,
  GET_CONSULTANT_FILES,
  GET_DOCTOR_FORMS,
  GET_EMAIL_TRANSLATION,
  GET_EMAIL_TRANSLATIONS,
  GET_ENCRYPTION_JOBS,
  GET_EVENTS,
  GET_FILES,
  GET_INSURANCE,
  GET_LOOKER_EMBED_SESSION,
  GET_MANUALLY_ADDED_CAREPROVIDERS,
  GET_NATIVE_TRANSLATION,
  GET_NATIVE_TRANSLATIONS,
  GET_ONPREMISE_BUNDLE_LINK,
  GET_ONPREMISE_BUNDLE_LINK_CARESEEKER,
  GET_ONPREMISE_CONFIG,
  GET_ONPREMISE_CONFIG_FOR_CARESEEKER,
  GET_ONPREMISE_IMPORTS,
  GET_ONPREMISE_LOGS,
  GET_PATIENT,
  GET_PATIENTS_NEW_LIGHT,
  GET_PREDICT_SOCIAL_WORKER_ACCOUNTS,
  GET_PRODUCT_TOUR,
  GET_PROVIDER_SEARCH_CONSULTANT,
  GET_PROVIDER_SEARCH_LISTING,
  GET_PROVIDER_SEARCH_PATIENTS,
  GET_PROVIDER_SEARCH_PROVIDER,
  GET_PROVIDER_SEARCH_REQUEST_OVERVIEW,
  GET_RECEIVER_ACCOUNTS,
  GET_RECEIVER_DOCS_API_PUBLIC_KEYS,
  GET_SEARCH_MERGE_MAP_REQUESTS,
  GET_SEARCH_MERGE_REQUESTS,
  GET_SEARCH_MERGE_TOTALS,
  GET_SELECTABLE_CAREPROVIDERS,
  GET_SENDER_RECEIVER_NOTE,
  GET_SENDER_RECEIVER_RULES,
  GET_SENDER_USER_MANAGEMENT_FILE,
  GET_SOCIAL_WORKER_ACCOUNTS,
  GET_SSO_INTEGRATION_IMPORT,
  GET_SSO_INTEGRATION_RIGHTS,
  GET_STATIC_SEARCH_CANDIDATES,
  GET_STATIONS_FOR_ACCOUNT_ROLE,
  GET_TRANSITIONAL_CARE_REQUESTS,
  GET_TRANSLATION,
  GET_TRANSLATIONS,
  GET_VERSIONS,
  LOGIN_ACTIVATION_PROVIDER_SEARCH_ACCOUNT,
  SEALD_GROUP_SESSIONS_CHECK,
  SEARCH_PATIENTS,
  ZIPCODE_INSIDE_CATCHMENT,
} from "core/apollo/graphql";
import {
  QUERY_PROGRESS_NOT_STARTED,
  REPOSITORY_VOICE,
  SEARCH_MERGE_PRINT_SECTIONS_PARAM,
  SEARCH_TABLE_ACCEPTED,
  SEARCH_TABLE_CONTACTED,
  SEARCH_TABLE_DECLINED,
  SEARCH_TABLE_FILTERED,
  SEARCH_TABLE_POTENTIAL,
  SEARCH_TABLE_REJECTED,
  SEARCH_TABLE_ROW_COUNT_10,
  SEARCH_TABLE_SORTING_DEFAULT,
  SEARCH_TABLE_TABLES,
  SEARCH_TABLE_VALIDATED,
  SORTING_MAP,
  TABLE_PAYLOAD_ACCEPTED,
  TABLE_PAYLOAD_CONTACTED,
  TABLE_PAYLOAD_DECLINED,
  TABLE_PAYLOAD_FILTERED,
  TABLE_PAYLOAD_POTENTIAL,
  TABLE_PAYLOAD_REJECTED,
  TABLE_PAYLOAD_VALIDATED,
} from "core/consts";
import { useSafeState } from "core/hooks";
import { getAuctionForDynamo } from "core/model/auctions";
import { useApolloEncryptionContext } from "core/model/patients/encryption/utils";
import { getErrorMessage } from "core/model/utils/errors";
import { objectToQueryParamString } from "core/model/utils/strings";
import { getReferrer } from "core/model/utils/urls";
import {
  AcceptedTablePayload,
  Account,
  AdmissionsActivation,
  ApolloCacheData,
  Auction,
  AuctionForm,
  AuctionRequest,
  AvailableSearchTypes,
  BedrockPlaygroundPage,
  Candidates,
  Careprovider,
  CareproviderAdmissionPage,
  CareproviderDashboardCounts,
  CareproviderFiles,
  Careseeker,
  Chat,
  ChatListItem,
  ClinicalConsultation,
  ClinicalPatientConnection,
  ClinicDashboardCounts,
  Company,
  ConsultantEventPage,
  ContactedTablePayload,
  DeclinedTablePayload,
  EmbedCookielessSessionAcquireResponse,
  EncryptionContext,
  EncryptionJob,
  Event,
  FileParams,
  Filev2,
  FilteredTablePayload,
  GetAccountsForCareproviderPayload,
  GetAccountsForCareproviderVariables,
  GetAccountsForCareseekerPayload,
  GetAccountsForCareseekerVariables,
  GetCareproviderRequestsNewPayload,
  GetUserManagmentFileParams,
  GroupSessionsResponse,
  Insurance,
  Location,
  NotificationEvent,
  OnPremImport,
  OnpremiseConfig,
  Page,
  PaginatedQueryResult,
  Patient,
  PatientSearchItem,
  PotentialReceiversTablePayload,
  ProductTourCompletion,
  ProviderBlacklistingRule,
  ProviderSearchActivation,
  ProviderSearchListing,
  ProviderSearchPatients,
  ProviderSearchProvider,
  ProviderSearchRequestsOverview,
  ReceiverDocsApiPublicKeys,
  RejectedTablePayload,
  SearchMergePayload,
  SearchTable,
  SearchTablePagination,
  SearchTableTotals,
  SelectableCareprovider,
  SenderReceiverNote,
  SessionKeyContext,
  SSOIntegration,
  SSOIntegrationsRight,
  TableCount,
  TablePageState,
  TablePayload,
  TableSortValue,
  TransitionalCareImportedAuctionRequest,
  ValidatedTablePayload,
  Version,
  WarningsPayload,
} from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import {
  EmailTranslation,
  RepositoryType,
  TranslationType,
} from "dsl/ecosystems/AcpTranslations/types";
import type { DoctorDashboardTableRow } from "dsl/ecosystems/DoctorDashboard/DoctorDashboardTable";
import { mapClinicalPatientToTableRow } from "dsl/ecosystems/DoctorDashboard/utils";
import { AssessmentStepTypeName } from "dsl/ecosystems/PatientAssessment/AssessmentStep";
import { useProviderSearchFilterContext } from "dsl/molecules/ProviderSearchAppWrapper";
import { ProductTourKey } from "dsl/molecules/useProductTour";
import {
  USER_FILTER_START_DATE,
  USER_FILTER_TREATMENT_LOCATION,
} from "dsl/organisms/Filters/consts";
import { ProviderSearchFilters } from "dsl/organisms/Filters/ProviderSearchApp/types";
import {
  ReceiverFilters,
  ReverseSearchResult,
} from "dsl/organisms/Filters/ReceiverApp/types";
import { SenderFilters } from "dsl/organisms/Filters/SenderApp/types";
import toFilterQueryString from "dsl/organisms/Filters/utils/toQueryString";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { useHandleAutoRefreshComponent } from "../subscriptions";
import { getFetchPolicy } from "../utils";
import {
  getQueryProgress,
  SafeQueryHookOptions,
  useGenericQuery,
  useGetAuctionRequestFetchPolicy,
  useLazyQuery,
  usePolling,
} from "./utils";

export type GetDoctorFormsData = {
  auction: {
    __typename: string;
    id: number;
    patient: { external_id: string | null };
    forms?: AuctionForm[];
    seald_encryption_context?: EncryptionContext;
    session_key_context?: SessionKeyContext;
  };
};
type GetDoctorFormsVariables = {
  auctionId: number | undefined;
  careseekerId: number;
};

export type GetChatsData = {
  chatsPage: {
    chats: ChatListItem[];
    has_more: boolean;
    next_cursor: string | null;
  };
};

export type GetChatsVariables = {
  params: string;
};

export type GetChatData = {
  chat?: Chat;
};

export type GetChatVariables = {
  id: number | undefined;
};

export type UseClinicalPatientsOptions = {
  careseekerId: number | undefined;
  page: number;
  rowsPerPage: number;
  search?: string;
};

export type UseClinicalPatientsResult =
  PaginatedQueryResult<DoctorDashboardTableRow> & {
    refetch: () => Promise<unknown>;
  };

export function useGetAssessmentFormFields({
  formName,
  input,
  revision,
}: {
  formName: AssessmentStepTypeName;
  input: ReturnType<typeof getAuctionForDynamo>;
  revision: number;
}) {
  return useLazyQuery<
    { assessmentFormFields: string[] },
    Partial<{
      formName: AssessmentStepTypeName;
      input: ReturnType<typeof getAuctionForDynamo>;
      revision: number | undefined;
    }>
  >(GET_ASSESSMENT_FORM_FIELDS, {
    variables: {
      formName,
      input,
      revision,
    },
  });
}

export const useGetPatientQuery = ({
  decrypt,
  options = {},
  patientId,
  polling = false,
}: {
  decrypt?: boolean;
  options?: SafeQueryHookOptions<{ patient: Patient }, { id: number }>;
  patientId: number;
  polling?: boolean;
}) => {
  const encryptionContext = useApolloEncryptionContext(
    decrypt ? { decrypt } : undefined,
  );

  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery<
    { patient: Patient },
    { id: number }
  >(GET_PATIENT, {
    context: { encryptionContext },
    variables: { id: patientId },
    ...options,
  });
  usePolling({ shouldPoll: polling, startPolling, stopPolling });

  return [
    getQueryProgress(loading, error, options.skip),
    data?.patient,
    { error, refetch },
  ] as const;
};

export const useGetNewPatientsQuery = ({
  careseekerId,
  count,
  filters,
  start,
}: {
  careseekerId: number | null;
  count?: number;
  filters: SenderFilters;
  start?: number;
  statusList?: Array<number>;
}) => {
  const queryArguments = useMemo(() => {
    let queryFilters = filters
      ? toFilterQueryString(filters, { byTab: true })
      : "";
    if (start != null && count)
      queryFilters = `${queryFilters}&start=${start}&count=${count}`;
    return queryFilters;
  }, [start, count, filters]);

  const variables = useMemo(
    () => ({
      arguments: queryArguments,
      id: careseekerId,
    }),
    [careseekerId, queryArguments],
  );

  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const { data, error, loading, refetch } = useQuery<{
    patientsNew: {
      patients: Array<Patient>;
      remaining: number;
      total: number;
    };
  }>(GET_PATIENTS_NEW_LIGHT, {
    context: { encryptionContext },
    variables,
  });

  return [
    getQueryProgress(loading, error),
    data?.patientsNew,
    { error, refetch },
  ] as const;
};

export function useSearchPatientsLazy() {
  const [executeQuery, { data, error, loading }] = useApolloLazyQuery<
    { searchPatients: { patients: PatientSearchItem[] } },
    { filters: string }
  >(SEARCH_PATIENTS, {
    fetchPolicy: "no-cache",
  });

  const searchPatients = useCallback(
    (options: { variables: { filters: string } }) => {
      return executeQuery({
        variables: options.variables,
      });
    },
    [executeQuery],
  );

  return [
    searchPatients,
    getQueryProgress(loading, error),
    data?.searchPatients.patients ?? [],
  ] as const;
}

export const useGetRequestsForProviderQuery = ({
  count,
  filters,
  providerId,
  start,
}: {
  count?: number;
  filters: ReceiverFilters;
  providerId: number | null | undefined;
  start?: number;
}) => {
  const encryptionContext = useApolloEncryptionContext({
    decrypt: true,
  });

  const queryArguments = useMemo(() => {
    let args = toFilterQueryString(filters, { byTab: true });

    if (start != null && count) args = `${args}&start=${start}&count=${count}`;

    return args;
  }, [count, filters, start]);

  const { data, error, loading, refetch } = useQuery<
    GetCareproviderRequestsNewPayload,
    { arguments: string; id: number | null | undefined }
  >(GET_CAREPROVIDER_REQUESTS_NEW({ withRequestsLight: !start }), {
    context: { encryptionContext },
    variables: {
      arguments: queryArguments,
      id: providerId,
    },
  });

  return [
    getQueryProgress(loading, error),
    data?.careproviderRequestsNew,
    { error, refetch },
  ] as const;
};

export const useGetRequestsCountForProviderQuery = ({
  fetchPolicy = "no-cache",
  filters,
  nextFetchPolicy,
  providerId,
}: {
  fetchPolicy?: WatchQueryFetchPolicy;
  filters?: ReceiverFilters;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  providerId: number | null | undefined;
}) => {
  const queryFilters = useMemo(
    () => (filters ? toFilterQueryString(filters, { byTab: false }) : ""),
    [filters],
  );

  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery<
    { requestCount: CareproviderDashboardCounts },
    { filters: string; id: number | null | undefined }
  >(GET_CAREPROVIDER_REQUESTS_COUNT, {
    fetchPolicy,
    nextFetchPolicy,
    variables: {
      filters: queryFilters,
      id: providerId,
    },
  });

  usePolling({ dependencies: [queryFilters], startPolling, stopPolling });

  const requestCount = data?.requestCount;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, requestCount, { error, refetch }] as const;
};

export const useGetPatientCounts = ({
  careseekerId,
  fetchPolicy = "no-cache",
  filters,
  nextFetchPolicy,
}: {
  careseekerId: number | null | undefined;
  fetchPolicy?: WatchQueryFetchPolicy;
  filters?: SenderFilters;
  nextFetchPolicy?: WatchQueryFetchPolicy;
}) => {
  const queryFilters = useMemo(
    () => (filters ? toFilterQueryString(filters, { byTab: false }) : ""),
    [filters],
  );
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery<
    { patientCount: ClinicDashboardCounts },
    { filters: string; id: number | null | undefined }
  >(GET_CARESEEKER_PATIENTS_COUNT, {
    fetchPolicy,
    nextFetchPolicy,
    variables: {
      filters: queryFilters,
      id: careseekerId,
    },
  });

  usePolling({
    dependencies: [queryFilters],
    startPolling,
    stopPolling,
  });

  const patientCount = data?.patientCount;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, patientCount, { error, refetch }] as const;
};

export function useGetTranslation(
  id: string,
  repo: RepositoryType,
  options: SafeQueryHookOptions<
    { translation: TranslationType },
    { id: string }
  > = {},
) {
  const { data, error, loading, refetch } = useQuery<
    { translation: TranslationType },
    { id: string }
  >(repo === REPOSITORY_VOICE ? GET_NATIVE_TRANSLATION : GET_TRANSLATION, {
    variables: { id },
    ...options,
  });

  const translation = data?.translation;
  const queryProgress = getQueryProgress(loading, error, options.skip);
  return [queryProgress, translation, { error, refetch }] as const;
}

export function useGetSenderReceiverRules(options: {
  [index: string]: number | undefined;
  careprovider_id?: number;
  careseeker_id?: number;
}) {
  const query = objectToQueryParamString(options);
  const { data, error, loading, refetch } = useQuery<
    { senderReceiverRules: ProviderBlacklistingRule[] },
    { params: string }
  >(GET_SENDER_RECEIVER_RULES, {
    variables: { params: query },
  });
  const senderReceiverRules = data?.senderReceiverRules;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, senderReceiverRules, { error, refetch }] as const;
}

export function useGetOnpremiseConfig({
  configId,
}: SafeQueryHookOptions<
  { onpremiseConfig: OnpremiseConfig },
  { configId: number | undefined }
> & {
  configId?: number;
}) {
  const toast = useToast();
  const { data, error, loading, refetch } = useQuery<
    { onpremiseConfig: OnpremiseConfig },
    { configId: number | undefined }
  >(GET_ONPREMISE_CONFIG, {
    skip: !configId || configId <= 0,
    variables: { configId },
  });

  useEffect(() => {
    if (error) toast({ color: "danger", message: error.message });
  }, [error]);

  const queryProgress = getQueryProgress(loading, error);
  const onpremiseConfig = data?.onpremiseConfig;

  return [queryProgress, onpremiseConfig, { error, refetch }] as const;
}

export function useGetOnpremiseConfigForCareseeker({
  careseekerId,
  skip,
}: SafeQueryHookOptions<
  { onpremiseConfig: OnpremiseConfig },
  { careseekerId: number | undefined }
> & {
  careseekerId: number | undefined;
}) {
  const toast = useToast();
  const { data, error, loading, refetch } = useQuery<
    { onpremiseConfig: OnpremiseConfig },
    { careseekerId: number | undefined }
  >(GET_ONPREMISE_CONFIG_FOR_CARESEEKER, {
    skip: skip || !careseekerId,
    variables: { careseekerId },
  });

  useEffect(() => {
    if (!error) return;
    console.error(`error fetching onpremise config: ${getErrorMessage(error)}`);
    toast({ color: "danger", message: error.message });
  }, [error]);

  const queryProgress = getQueryProgress(loading, error);
  const onpremiseConfig = data?.onpremiseConfig;

  return [queryProgress, onpremiseConfig, { error, refetch }] as const;
}

export function useGetOnpremiseImportsForCareseeker({
  careseekerId,
}: SafeQueryHookOptions<
  { onpremiseConfig: OnPremImport[] },
  { careseekerId: number }
> & {
  careseekerId: number;
}) {
  const toast = useToast();
  const { data, error, loading, refetch } = useQuery<
    { onpremiseConfig: OnPremImport[] },
    { careseekerId: number }
  >(GET_ONPREMISE_IMPORTS, {
    variables: { careseekerId },
  });

  useEffect(() => {
    if (error) toast({ color: "danger", message: error.message });
  }, [error]);

  const queryProgress = getQueryProgress(loading, error);
  const onpremiseConfig = data?.onpremiseConfig;

  return [queryProgress, onpremiseConfig, { error, refetch }] as const;
}

export function useGetTranslations(
  repo: RepositoryType,
  options: SafeQueryHookOptions<{
    translations: TranslationType[];
  }> = {},
) {
  const { data, error, loading, refetch } = useQuery<{
    translations: TranslationType[];
  }>(
    repo === REPOSITORY_VOICE ? GET_NATIVE_TRANSLATIONS : GET_TRANSLATIONS,
    options,
  );

  const translations = data?.translations;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, translations, { error, refetch }] as const;
}

export function useGetAccountsForCareseeker(
  options: SafeQueryHookOptions<
    GetAccountsForCareseekerPayload,
    GetAccountsForCareseekerVariables
  >,
) {
  const { data, error, loading, refetch } = useQuery<
    GetAccountsForCareseekerPayload,
    GetAccountsForCareseekerVariables
  >(GET_ACCOUNTS_FOR_CARESEEKER, {
    fetchPolicy: getFetchPolicy(options.fetchPolicy),
    ...options,
  });

  const payload = data?.getAccountsForCareseeker
    ? {
        ...data.getAccountsForCareseeker,
        total_accounts: data.getAccountsForCareseeker.total_accounts ?? 0,
        total_filtered_accounts:
          data.getAccountsForCareseeker.total_filtered_accounts ?? 0,
      }
    : {
        accounts: [],
        active_user_manager_accounts_count: 0,
        total_accounts: 0,
        total_filtered_accounts: 0,
      };
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, payload, { error, refetch }] as const;
}

export function useGetAccountsForCareprovider(
  options: SafeQueryHookOptions<
    GetAccountsForCareproviderPayload,
    GetAccountsForCareproviderVariables
  >,
) {
  const { data, error, loading, refetch } = useQuery<
    GetAccountsForCareproviderPayload,
    GetAccountsForCareproviderVariables
  >(GET_ACCOUNTS_FOR_CAREPROVIDER, {
    fetchPolicy: getFetchPolicy(options.fetchPolicy),
    ...options,
  });

  const payload = data?.getAccountsForCareprovider
    ? {
        ...data.getAccountsForCareprovider,
        total_accounts: data.getAccountsForCareprovider.total_accounts,
        total_filtered_accounts:
          data.getAccountsForCareprovider.total_filtered_accounts,
      }
    : {
        accounts: [],
        total_accounts: 0,
        total_filtered_accounts: 0,
      };
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, payload, { error, refetch }] as const;
}

export function useGetInternalAccounts(
  options: SafeQueryHookOptions<{
    accounts: Account[];
  }> = {},
) {
  const { data, error, loading, refetch } = useQuery<{
    accounts: Account[];
  }>(GET_ACCOUNTS, options);

  const accounts = data?.accounts;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, accounts, { error, refetch }] as const;
}

export const useGetReceiverAccounts = ({
  careproviderId,
  ...options
}: SafeQueryHookOptions<{ accounts: Account[] }, { id: number | undefined }> & {
  careproviderId: number | undefined;
}) => {
  const [queryProgress, data, { error, refetch }] = useGenericQuery<
    { accounts: Account[] },
    { id: number | undefined }
  >(GET_RECEIVER_ACCOUNTS, {
    skip: !careproviderId,
    variables: { id: careproviderId },
    ...options,
  });

  return [queryProgress, data?.accounts, { error, refetch }] as const;
};

export const useGetCareseekerAccounts = ({
  careseekerId,
  ...options
}: SafeQueryHookOptions<{ accounts: Account[] }, { id: number | undefined }> & {
  careseekerId: number | undefined;
}) => {
  const [queryProgress, data, { error, refetch }] = useGenericQuery<
    { accounts: Account[] },
    { id: number | undefined }
  >(GET_CARESEEKER_ACCOUNTS, {
    skip: !careseekerId,
    variables: { id: careseekerId },
    ...options,
  });

  return [queryProgress, data?.accounts ?? [], { error, refetch }] as const;
};

export function useGetCareseeker(
  id: number | undefined,
  queryOptions: {
    withAdminFields?: boolean;
    withConfig?: boolean;
    withProductFeatures?: boolean;
    withSSO?: boolean;
  } = {},
  options: SafeQueryHookOptions<
    { careseeker: Careseeker },
    { id: number | undefined }
  > = {},
) {
  const normalizedQueryOptions = {
    withAdminFields: false,
    withConfig: false,
    ...queryOptions,
  };

  const { data, error, loading, refetch } = useQuery<
    { careseeker: Careseeker },
    { id: number | undefined }
  >(GET_CARESEEKER(normalizedQueryOptions), {
    skip: !id,
    variables: { id },
    ...options,
  });

  const careseeker = data?.careseeker;
  const queryProgress = getQueryProgress(loading, error, !id);
  return [queryProgress, careseeker, { error, refetch }] as const;
}

export const useLazyGetCareseeker = ({
  fetchPolicy,
  queryOptions = {},
}: {
  fetchPolicy?: FetchPolicy;
  queryOptions?: {
    withAdminFields?: boolean;
    withConfig?: boolean;
  };
}) => {
  const [getCareseker] = useLazyQuery<{ careseeker: Careseeker }>(
    GET_CARESEEKER(queryOptions),
    { fetchPolicy },
  );

  return (id: number) => {
    return getCareseker({ id });
  };
};

export function useClinicalPatients(
  options: UseClinicalPatientsOptions,
): UseClinicalPatientsResult {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const { careseekerId, page, rowsPerPage, search = "" } = options;
  const start = (page - 1) * rowsPerPage;
  const { data, loading, refetch } = useQuery<{
    clinicalPatientConnection: ClinicalPatientConnection;
  }>(GET_CLINICAL_PATIENTS, {
    context: { encryptionContext },
    skip: careseekerId == null,
    variables: {
      careseekerId: careseekerId ?? 0,
      count: rowsPerPage,
      search: search.trim() || "",
      start,
    },
  });

  const connection = data?.clinicalPatientConnection;
  const rows =
    connection?.patients?.map(mapClinicalPatientToTableRow) ?? undefined;
  const total = connection?.total_items ?? 0;

  return {
    data: rows,
    isLoading: loading,
    refetch,
    total,
  };
}

export function useGetConsultations({
  patientId,
}: {
  patientId: number | undefined;
}) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [progress, data] = useGenericQuery<
    {
      clinicalConsultations: { clinical_consultations: ClinicalConsultation[] };
    },
    { patientId: number }
  >(GET_CLINICAL_CONSULTATIONS, {
    context: { encryptionContext },
    skip: !patientId,
    variables: { patientId: patientId ?? -1 },
  });

  const clinicalConsultations =
    data?.clinicalConsultations.clinical_consultations ?? [];

  return [progress, clinicalConsultations] as const;
}

export function useGetCareprovider({
  id,
  options = {},
  queryOptions = {},
  token,
}: {
  id: number | undefined;
  options?: SafeQueryHookOptions<
    { careprovider: Careprovider },
    { id: number | undefined }
  >;
  queryOptions?: {
    withContacts?: boolean;
    withEvents?: boolean;
    withInternalNotes?: boolean;
    withMetrics?: boolean;
    withRoles?: boolean;
  };
  token?: string;
}) {
  const { data, error, loading, refetch } = useQuery<
    { careprovider: Careprovider },
    { id: number | undefined }
  >(
    token
      ? GET_CAREPROVIDER_TOKEN({ ...queryOptions, token })
      : GET_CAREPROVIDER(queryOptions),
    {
      skip: !id,
      variables: { id },
      ...options,
    },
  );

  const careprovider = data?.careprovider;
  const queryProgress = getQueryProgress(loading, error, !id);
  return [queryProgress, careprovider, { error, refetch }] as const;
}

export type ManuallyAddedProvider = Pick<
  Careprovider,
  "id" | "name" | "address"
>;

export function useGetManuallyAddedCareproviders() {
  const { data, error, loading, refetch } = useQuery<{
    careproviders: Array<ManuallyAddedProvider>;
  }>(GET_MANUALLY_ADDED_CAREPROVIDERS);

  const careproviders = data?.careproviders;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, careproviders, { error, refetch }] as const;
}

export function useGetSelectableCareprovidersLazy({
  id,
  ...options
}: {
  id?: number;
  options?: Parameters<typeof useLazyQuery>[1];
} = {}) {
  return useLazyQuery<
    { careproviders: { id: number; name: string }[] },
    { id?: number }
  >(GET_SELECTABLE_CAREPROVIDERS, {
    fetchPolicy: "network-only",
    variables: { id },
    ...options,
  });
}

export function useGetAccount(
  id: number | undefined,
  options: SafeQueryHookOptions<
    { account: Account },
    { id: number | undefined }
  > = {},
) {
  const [queryProgress, data, { error, refetch }] = useGenericQuery<
    { account: Account },
    { id: number | undefined }
  >(GET_ACCOUNT, {
    skip: (id && id <= 0) || !id,
    variables: { id },
    ...options,
  });

  const account = data?.account;
  return [queryProgress, account, { error, refetch }] as const;
}

export function useGetCachedProviderQuery({
  careproviderId,
  token,
}: SafeQueryHookOptions<
  { careprovider: Careprovider | null },
  { id: number | undefined; token?: string }
> & {
  careproviderId: number | undefined;
  token?: string;
}) {
  const [progress, data] = useGenericQuery<
    { careprovider: Careprovider | null },
    { id: number | undefined; token?: string }
  >(
    token
      ? GET_CAREPROVIDER_TOKEN({ token, withEvents: false })
      : GET_CAREPROVIDER({ withEvents: false }),
    {
      fetchPolicy: "cache-only",
      skip: !careproviderId,
      variables: { id: careproviderId },
    },
  );
  return [progress, data?.careprovider] as const;
}

export function useGetCompany({
  id,
  options = {},
  token,
}: {
  id: number;
  options?: SafeQueryHookOptions<
    { company: Company },
    { id: number; token?: string }
  >;
  token?: string;
}) {
  const { data, error, loading, refetch } = useQuery<
    { company: Company },
    { id: number; token?: string }
  >(token ? GET_COMPANY_TOKEN : GET_COMPANY, {
    variables: { id, token },
    ...options,
  });

  const company = data?.company;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, company, { error, refetch }] as const;
}

export function useLazyGetCompany() {
  const [getCompany] = useLazyQuery<{ company: Company }, { id: number }>(
    GET_COMPANY,
  );
  return getCompany;
}

export function useLazyGetAccount() {
  const [getAccount] = useLazyQuery<{ account: Account }, { id: number }>(
    GET_ACCOUNT,
    { fetchPolicy: getFetchPolicy("network-only") },
  );
  return async (id: number): Promise<Account | undefined> => {
    const { data } = await getAccount({ id });
    return data?.account;
  };
}

export function useLazyGetCareprovider({
  fetchPolicy,
}: {
  fetchPolicy?: WatchQueryFetchPolicy;
}) {
  const [getCareprovider] = useLazyQuery<
    { careprovider: Careprovider },
    { id: number }
  >(GET_CAREPROVIDER(), {
    fetchPolicy: fetchPolicy,
  });
  return async (id: number): Promise<Careprovider | undefined> => {
    const { data } = await getCareprovider({ id });
    return data?.careprovider;
  };
}

export function useGetInsurance(
  id: number,
  options: SafeQueryHookOptions<{ insurance: Insurance }, { id: number }> = {},
) {
  const { data, error, loading, refetch } = useQuery<
    { insurance: Insurance },
    { id: number }
  >(GET_INSURANCE, {
    variables: { id },
    ...options,
  });

  const insurance = data?.insurance;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, insurance, { error, refetch }] as const;
}

export function getPatientEventQuery({
  auctionId,
  count,
  eventBlacklist,
  eventWhiteList,
  patientId,
  reverse = false,
  start,
  withEventLastSeens,
}: {
  auctionId?: number;
  count?: number;
  eventBlacklist?: Array<number>;
  eventWhiteList?: Array<number>;
  patientId: number;
  reverse?: boolean;
  start?: number;
  withEventLastSeens?: boolean;
}) {
  let query = reverse ? "reverse" : "";
  query += patientId != null ? `&patient_id=${patientId}` : "";
  query += auctionId != null ? `&auction_id=${auctionId}` : "";
  query += start != null ? `&start=${start}` : "";
  query += count != null ? `&count=${count}` : "";
  query += withEventLastSeens ? `&last_seens` : "";
  query += eventWhiteList ? `&event_types=${eventWhiteList.join(",")}` : "";
  query += eventBlacklist
    ? `&exclude_event_types=${eventBlacklist.join(",")}`
    : "";
  query += `&fields=careprovider`;

  const fetchPolicy: WatchQueryFetchPolicy = "network-only";

  return {
    fetchPolicy,
    query: GET_EVENTS({ withEventLastSeens }),
    variables: {
      params: query,
    },
  };
}

export function getAuctionRequestEventQuery({
  auctionRequestId,
  withEventLastSeens,
}: {
  auctionRequestId: number;
  withEventLastSeens?: boolean;
}) {
  const withEventLastSeensParam = withEventLastSeens ? `&last_seens` : "";

  const query = `reverse&auction_request_id=${auctionRequestId}${withEventLastSeensParam}&fields=careseeker,careprovider`;

  return {
    query: GET_EVENTS({ withEventLastSeens }),
    variables: {
      params: query,
    },
  };
}

const useGetEvents = ({
  count,
  eventBlacklist,
  eventWhiteList,
  fields,
  idParamName,
  idParamValue,
  start,
  withCounts = true,
}: {
  count?: number;
  eventBlacklist?: Array<number>;
  eventWhiteList?: Array<number>;
  fields?: string;
  idParamName: string;
  idParamValue: number;
  start?: number;
  withCounts?: boolean;
}) => {
  const idParam = `${idParamName}=${idParamValue}`;
  const startParam = start != null ? `&start=${start}` : "";
  const countParam = count != null ? `&count=${count}` : "";
  const withCountsParam = withCounts ? "" : "&without_counts";

  const { data, error, loading, refetch } = useQuery<
    { eventsPayload: Page<Event> },
    { params: string }
  >(GET_EVENTS({ withCounts }), {
    variables: {
      params: `${idParam}${startParam}${countParam}${
        fields ? `&fields=${fields}` : ""
      }${eventWhiteList ? `&event_types=${eventWhiteList.join(",")}` : ""}${
        eventBlacklist ? `&exclude_event_types=${eventBlacklist.join(",")}` : ""
      }${withCountsParam}`,
    },
  });

  const payload = data?.eventsPayload;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, payload, { error, refetch }] as const;
};

export const useGetAccountEvents = ({
  count,
  eventBlacklist,
  eventWhiteList,
  fields,
  id,
  start,
  withCounts,
}: {
  count?: number;
  eventBlacklist?: Array<number>;
  eventWhiteList?: Array<number>;
  fields?: string;
  id: number;
  start?: number;
  withCounts?: boolean;
}) => {
  return useGetEvents({
    count,
    eventBlacklist,
    eventWhiteList,
    fields,
    idParamName: "account_id",
    idParamValue: id,
    start,
    withCounts,
  });
};

export const useGetCompanyEvents = ({
  count,
  eventBlacklist,
  eventWhiteList,
  fields,
  id,
  start,
}: {
  count?: number;
  eventBlacklist?: Array<number>;
  eventWhiteList?: Array<number>;
  fields?: string;
  id: number;
  start?: number;
}) => {
  return useGetEvents({
    count,
    eventBlacklist,
    eventWhiteList,
    fields,
    idParamName: "company_id",
    idParamValue: id,
    start,
  });
};

export const useGetCareseekerEvents = ({
  count,
  eventBlacklist,
  eventWhiteList,
  fields,
  id,
  start,
  withCounts,
}: {
  count?: number;
  eventBlacklist?: Array<number>;
  eventWhiteList?: Array<number>;
  fields: string;
  id: number;
  start?: number;
  withCounts?: boolean;
}) => {
  return useGetEvents({
    count,
    eventBlacklist,
    eventWhiteList,
    fields,
    idParamName: "careseeker_id",
    idParamValue: id,
    start,
    withCounts,
  });
};

export const useGetCareproviderEvents = ({
  count,
  eventBlacklist,
  eventWhiteList,
  fields,
  id,
  start,
  withCounts,
}: {
  count?: number;
  eventBlacklist?: Array<number>;
  eventWhiteList?: Array<number>;
  fields: string;
  id: number;
  start?: number;
  withCounts?: boolean;
}) => {
  return useGetEvents({
    count,
    eventBlacklist,
    eventWhiteList,
    fields,
    idParamName: "careprovider_id",
    idParamValue: id,
    start,
    withCounts,
  });
};

export function useGetAuctionRequestEvents({
  id,
  withEventLastSeens,
}: {
  id: number;
  withEventLastSeens?: boolean;
}) {
  const { query, variables } = getAuctionRequestEventQuery({
    auctionRequestId: id,
    withEventLastSeens,
  });

  const { data, error, loading, refetch } = useQuery<
    {
      eventsPayload: {
        events: Array<Event>;
        remaining: number;
        total: number;
      };
    },
    { params: string }
  >(query, { fetchPolicy: "network-only", variables });

  const payload = data?.eventsPayload;
  const queryProgress = getQueryProgress(loading, error);
  return [
    queryProgress,
    payload,
    { error, querySignature: { query, variables }, refetch },
  ] as const;
}

export function useGetPatientEvents({
  auctionId,
  count,
  eventIds,
  excludedEventIds,
  patientId,
  reverse = false,
  start,
  withEventLastSeens,
}: {
  auctionId: number;
  count?: number;
  eventIds?: Array<number>;
  excludedEventIds?: Array<number>;
  patientId: number;
  reverse?: boolean;
  start?: number;
  withEventLastSeens?: boolean;
}) {
  const { query, variables } = getPatientEventQuery({
    auctionId,
    count,
    eventBlacklist: excludedEventIds,
    eventWhiteList: eventIds,
    patientId,
    reverse,
    start,
    withEventLastSeens,
  });

  const { data, error, loading, refetch } = useQuery<
    {
      eventsPayload: {
        events: Array<Event>;
        remaining: number;
        total: number;
      };
    },
    { params: string }
  >(query, { fetchPolicy: "network-only", variables });

  const payload = data?.eventsPayload;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, payload, { error, refetch }] as const;
}

export function useLazyGetPatientEvents({
  count,
  eventIds,
  excludedEventIds,
  reverse = false,
  start,
  withEventLastSeens,
}: {
  count?: number;
  eventIds?: Array<number>;
  excludedEventIds?: Array<number>;
  reverse?: boolean;
  start?: number;
  withEventLastSeens?: boolean;
}) {
  const [getEvents] = useLazyQuery<{
    eventsPayload: {
      events: Array<Event>;
      remaining: number;
      total: number;
    };
  }>(GET_EVENTS());

  function getPatientEvents({
    auctionId,
    patientId,
  }: {
    auctionId: number;
    patientId: number;
  }) {
    const { variables } = getPatientEventQuery({
      auctionId,
      count,
      eventBlacklist: excludedEventIds,
      eventWhiteList: eventIds,
      patientId,
      reverse,
      start,
      withEventLastSeens,
    });
    return getEvents(variables);
  }

  return getPatientEvents;
}

export function useGetAuction(
  id: number | null | undefined,
  options: SafeQueryHookOptions<
    { auction: Auction },
    { auctionId: number | null | undefined }
  > & { withWinnerContacts?: boolean } = {},
) {
  const { withWinnerContacts, ...restOptions } = options;
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const query = withWinnerContacts
    ? GET_AUCTION_WITH_WINNER_CONTACTS
    : GET_AUCTION;
  const { data, error, loading, refetch } = useQuery<
    { auction: Auction },
    { auctionId: number | null | undefined }
  >(query, {
    context: { encryptionContext },
    fetchPolicy: "network-only",
    variables: { auctionId: id },
    ...restOptions,
  });

  const auction = data?.auction;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, auction, { error, refetch }] as const;
}

export function useLazyGetAuction() {
  const encryptionContext = useApolloEncryptionContext();
  const [getAuction] = useLazyQuery<
    { auction: Auction },
    { auctionId: number | null | undefined }
  >(GET_AUCTION, {
    encryptionContext,
    fetchPolicy: "network-only",
  });

  return (id: number | null | undefined) => getAuction({ auctionId: id });
}

export function useGetSenderReceiverNote({
  careproviderId,
  careseekerId,
}: {
  careproviderId: number | null | undefined;
  careseekerId: number | null | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<
    { senderReceiverNote: SenderReceiverNote },
    {
      careproviderId: number | null | undefined;
      careseekerId: number | null | undefined;
    }
  >(GET_SENDER_RECEIVER_NOTE, {
    variables: { careproviderId, careseekerId },
  });

  const queryProgress = getQueryProgress(loading, error);
  const payload = data?.senderReceiverNote;

  return [queryProgress, payload, { error, refetch }] as const;
}

export function useLazyGetCareproviderNotifications() {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [getNotifications, queryProgress] = useLazyQuery<{
    careproviderNotifications: {
      events: NotificationEvent[];
      remaining: number;
    };
  }>(GET_CAREPROVIDER_NOTIFICATIONS, {
    encryptionContext,
  });

  const getPaginatedNotifications = useCallback(
    async ({ count = 15, start = 0 }: { count: number; start: number }) => {
      const { data } = await getNotifications({ count, start });

      return [queryProgress, data?.careproviderNotifications] as const;
    },
    [getNotifications],
  );

  return getPaginatedNotifications;
}

export function useGetCareproviderFileList({
  careproviderId,
  careproviderToken,
}: {
  careproviderId: number;
  careproviderToken?: string;
}) {
  const { data, error, loading, refetch } = useQuery<
    { getFiles: CareproviderFiles },
    { careproviderId: number; params: string }
  >(GET_CAREPROVIDER_FILES, {
    variables: {
      careproviderId,
      params: careproviderToken ? `?token=${careproviderToken}` : "",
    },
  });

  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, data?.getFiles, { error, refetch }] as const;
}

export function useLazyGetCareproviderInfo({
  accountId,
}: {
  accountId: number;
}) {
  const [getCareproviderInfo, loading] = useLazyQuery<{
    getCareproviderInfo: {
      careprovider_info: {
        [id: number]: {
          requests_in_new_tab: boolean;
        };
      };
    };
  }>(GET_CAREPROVIDER_INFO, { fetchPolicy: "network-only" });

  const fetchCareproviderRequestInfo = async () => {
    try {
      const { data } = await getCareproviderInfo({
        id: accountId,
      });
      return data?.getCareproviderInfo?.careprovider_info;
    } catch (err) {
      console.error(
        `error fetching careproviderinfo for account ${accountId}: ${err}`,
      );
      return [];
    }
  };

  return [loading, fetchCareproviderRequestInfo] as const;
}

export function useGetCareproviderDPAFileLazy({
  careproviderId,
}: {
  careproviderId: number;
}) {
  return useLazyQuery<
    { file: { apistring: string | undefined } },
    { careproviderId: number }
  >(GET_CAREPROVIDER_DPA, {
    fetchPolicy: "network-only",
    variables: { careproviderId },
  });
}

export function useGetUserManagementFileLazy({
  id,
}: GetUserManagmentFileParams) {
  return useLazyQuery<
    { file: { url: string | undefined } },
    GetUserManagmentFileParams
  >(GET_SENDER_USER_MANAGEMENT_FILE, {
    fetchPolicy: "network-only",
    variables: { id },
  });
}

export const useGetFilesQuery = ({ params }: { params: FileParams }) => {
  const [progress, data, rest] = useGenericQuery<
    { files: Filev2[] | undefined },
    { params: FileParams }
  >(GET_FILES, {
    fetchPolicy: getFetchPolicy("network-only"),
    variables: { params },
  });

  return [progress, data?.files, rest] as const;
};

export function useGetSelectableCareproviders({
  fileId,
  ...options
}: {
  fileId: Filev2["id"];
} & SafeQueryHookOptions<
  { careproviders: SelectableCareprovider[] },
  { id: number }
>) {
  const { data, error, loading, refetch } = useQuery<
    { careproviders: SelectableCareprovider[] },
    { id: number }
  >(GET_SELECTABLE_CAREPROVIDERS, {
    variables: { id: fileId },
    ...options,
    fetchPolicy: options.fetchPolicy ?? "network-only",
    skip: !fileId || options.skip,
  });
  const queryProgress = getQueryProgress(loading, error);
  const payload = data?.careproviders;

  return [queryProgress, payload, { error, refetch }] as const;
}

export function useGetFilesLazyQuery({ params }: { params: FileParams }) {
  return useLazyQuery<{ files: Filev2[] | undefined }, { params: FileParams }>(
    GET_FILES,
    {
      fetchPolicy: "network-only",
      variables: { params },
    },
  );
}

export function useGetReverseSearches({
  careproviderId,
}: {
  careproviderId: number;
}) {
  const { data, error, loading, refetch } = useQuery<
    { reverseSearch: ReverseSearchResult },
    { careproviderId: number }
  >(GET_CAREPROVIDER_REVERSE_SEARCHES, {
    variables: { careproviderId },
  });
  const queryProgress = getQueryProgress(loading, error);
  const payload = data?.reverseSearch;

  return [queryProgress, payload, { error, refetch }] as const;
}

export function useGetBundleLink({ configId }: { configId?: number }) {
  const { data, error, loading, refetch } = useQuery<
    { link: { apistring: string } },
    { configId: number | undefined }
  >(GET_ONPREMISE_BUNDLE_LINK, {
    fetchPolicy: "network-only",
    variables: { configId },
  });
  const queryProgress = getQueryProgress(loading, error);
  const bundleDownloadLink = data?.link.apistring;

  return [queryProgress, bundleDownloadLink, { error, refetch }] as const;
}

export function useGetBundleLinkForCareseeker({
  careseekerId,
}: {
  careseekerId?: number;
}) {
  const { data, error, loading, refetch } = useQuery<
    { link: { apistring: string } },
    { careseekerId?: number }
  >(GET_ONPREMISE_BUNDLE_LINK_CARESEEKER, {
    fetchPolicy: "network-only",
    variables: { careseekerId },
  });
  const queryProgress = getQueryProgress(loading, error);
  const bundleDownloadLink = data?.link.apistring;

  return [queryProgress, bundleDownloadLink, { error, refetch }] as const;
}

export function useGetStaticSearchCandidates({
  auctionId,
}: {
  auctionId: number;
}) {
  return useLazyQuery<{ searchCandidates: Candidates }, { auctionId: number }>(
    GET_STATIC_SEARCH_CANDIDATES,
    {
      variables: { auctionId },
    },
  );
}

export const useGetCareproviderWarnings = ({
  auctionId,
  careproviderId,
}: {
  auctionId: number;
  careproviderId?: number;
}) => {
  const { data, error, loading, refetch } = useQuery<
    { careproviderWarnings: WarningsPayload | undefined },
    {
      auctionId: number;
      careproviderId?: number;
    }
  >(GET_CAREPROVIDER_WARNINGS, {
    fetchPolicy: "no-cache",
    skip: !careproviderId || !auctionId,
    variables: { auctionId, careproviderId },
  });

  const queryProgress = careproviderId
    ? getQueryProgress(loading, error)
    : QUERY_PROGRESS_NOT_STARTED;

  return [
    queryProgress,
    data?.careproviderWarnings,
    { error, refetch },
  ] as const;
};

export function useGetProviderPatientRequest(
  careproviderId: number | undefined,
): (patientId: number) => Promise<number | undefined> {
  const [getProviderPatientRequest] = useLazyQuery<
    { providerPatientRequest: { id: number } },
    { careproviderId: number; patientId: number }
  >(GET_CAREPROVIDER_PATIENT_REQUEST);

  const fetchProviderPatientRequestId = async (
    patientId: number,
  ): Promise<number | undefined> => {
    if (!careproviderId) {
      throw new Error(
        "careproviderId is required to fetch provider patient request",
      );
    }

    try {
      const { data } = await getProviderPatientRequest({
        careproviderId,
        patientId,
      });
      return data?.providerPatientRequest?.id;
    } catch (error) {
      console.error("Failed to fetch provider patient request:", error);
      throw error;
    }
  };

  return fetchProviderPatientRequestId;
}

const INITIAL_TABLE_PAGE_STATE: TablePageState = {
  count: SEARCH_TABLE_ROW_COUNT_10,
  page: 1,
  sortBy: SEARCH_TABLE_SORTING_DEFAULT,
};

const getSortingTableSortingParam = ({
  sortValue,
  table,
}: {
  sortValue: TableSortValue;
  table: SearchTable;
}) => {
  if (!sortValue || sortValue === SEARCH_TABLE_SORTING_DEFAULT) return "";

  const sortParam = SORTING_MAP?.[sortValue];
  return `&${table}_sort=${sortParam}`;
};

export const getSearchMergeTableQuery = ({
  pageState = INITIAL_TABLE_PAGE_STATE,
  table,
}: {
  pageState?: TablePageState;
  table: SearchTable;
}) => {
  const pageToUse = pageState.page - 1;

  if (
    // Tables that are not paginated
    ([SEARCH_TABLE_VALIDATED, SEARCH_TABLE_ACCEPTED] as SearchTable[]).includes(
      table,
    )
  )
    return table;

  const sortParam = getSortingTableSortingParam({
    sortValue: pageState.sortBy,
    table,
  });

  return `${table}_start=${pageToUse * pageState.count}&${table}_count=${
    pageState.count
  }${sortParam}`;
};

export const getSearchMergeParams = ({
  pagination,
}: {
  pagination: SearchTablePagination | null;
}) => {
  if (!pagination) return "";

  let queryString = "";

  Object.entries(pagination).forEach(([table, pageState], index) => {
    queryString += `${index === 0 ? "" : "&"}${getSearchMergeTableQuery({
      pageState,
      table: table as SearchTable,
    })}`;
  });

  return queryString;
};

const searchMergePayloadMapping: Record<SearchTable, TablePayload> = {
  [SEARCH_TABLE_ACCEPTED]: TABLE_PAYLOAD_ACCEPTED,
  [SEARCH_TABLE_CONTACTED]: TABLE_PAYLOAD_CONTACTED,
  [SEARCH_TABLE_DECLINED]: TABLE_PAYLOAD_DECLINED,
  [SEARCH_TABLE_FILTERED]: TABLE_PAYLOAD_FILTERED,
  [SEARCH_TABLE_POTENTIAL]: TABLE_PAYLOAD_POTENTIAL,
  [SEARCH_TABLE_REJECTED]: TABLE_PAYLOAD_REJECTED,
  [SEARCH_TABLE_VALIDATED]: TABLE_PAYLOAD_VALIDATED,
};

const fillTableCaches = ({
  auctionId,
  client,
  data,
  pagination,
}: {
  auctionId: number;
  client: ApolloClient<ApolloCacheData>;
  data:
    | {
        searchMergeRequests: SearchMergePayload;
      }
    | undefined;
  pagination: SearchTablePagination | null;
}) => {
  if (!data || !pagination) return;

  const tableTotals: SearchTableTotals & { __typename: string } = {
    __typename: "TableTotals",
  };

  Object.entries(pagination).forEach(([table, pageState]) => {
    const tablePayload = searchMergePayloadMapping[table as SearchTable];
    const queryParams = getSearchMergeTableQuery({
      pageState,
      table: table as SearchTable,
    });
    const tableData = data?.searchMergeRequests?.[tablePayload];

    tableTotals[tablePayload] =
      data?.searchMergeRequests?.[tablePayload]?.total ?? 0;

    if (
      tablePayload === TABLE_PAYLOAD_VALIDATED &&
      data?.searchMergeRequests?.[tablePayload]?.request?.id
    ) {
      tableTotals[tablePayload] = 1;
    }
    client.writeQuery({
      data: {
        searchMergeRequests: {
          __typename: "SearchMerge",
          [tablePayload]: tableData,
        } as SearchMergePayload & { __typename: string },
      },
      query: GET_SEARCH_MERGE_REQUESTS({
        [table as SearchTable]: true,
      }),
      variables: {
        auctionId,
        queryParams,
      },
    });
  });

  client.writeQuery({
    data: {
      searchMergeTableTotals: tableTotals,
    },
    query: GET_SEARCH_MERGE_TOTALS,
    variables: {
      auctionId,
    },
  });
};

const getSearchTableStorageKey = ({
  auctionId,
  table,
}: {
  auctionId: number;
  table: SearchTable;
}) => `${auctionId}-${table}-search-merge-pagination`;

export const getSearchTablePagination = (
  auctionId: number,
): SearchTablePagination => {
  const pagination = {} as SearchTablePagination;
  SEARCH_TABLE_TABLES.forEach((table) => {
    const storedTablePage = sessionStorage.getItem(
      getSearchTableStorageKey({ auctionId, table }),
    );

    pagination[table] = storedTablePage
      ? JSON.parse(storedTablePage)
      : INITIAL_TABLE_PAGE_STATE;
  });

  return pagination;
};

export function useGetPrintableSearchMergeRequests({
  auctionId,
  printSections,
}: {
  auctionId: number;
  printSections: string | null;
}): [SearchMergePayload | undefined] {
  const fragments: Record<SearchTable, boolean> = {
    [SEARCH_TABLE_ACCEPTED]: true,
    [SEARCH_TABLE_CONTACTED]: true,
    [SEARCH_TABLE_DECLINED]: true,
    [SEARCH_TABLE_FILTERED]: true,
    [SEARCH_TABLE_POTENTIAL]: true,
    [SEARCH_TABLE_REJECTED]: true,
    [SEARCH_TABLE_VALIDATED]: true,
  };

  const { data } = useQuery<{
    searchMergeRequests: SearchMergePayload;
  }>(GET_SEARCH_MERGE_REQUESTS(fragments), {
    fetchPolicy: "network-only",
    skip: auctionId === -1 || !printSections?.length,
    variables: {
      auctionId,
      queryParams: `${SEARCH_MERGE_PRINT_SECTIONS_PARAM}=${printSections}`,
    },
  });

  return [data?.searchMergeRequests];
}

export function useGetSearchMergeMapRequests({
  auctionId,
}: {
  auctionId: number;
}) {
  const { data, loading } = useQuery<
    { requests: AuctionRequest[] },
    { auctionId: number }
  >(GET_SEARCH_MERGE_MAP_REQUESTS, {
    fetchPolicy: "network-only",
    variables: { auctionId },
  });

  const requests = loading === false ? data?.requests ?? [] : undefined;
  return [requests, loading] as const;
}

export function usePreloadSearchMergeRequests({
  auctionId,
}: {
  auctionId: number;
}) {
  const client = useApolloClient();
  const translations = useTranslations();
  const pagination = getSearchTablePagination(auctionId);
  const [searchMergeContext, setSearchMergeContext] = useSafeState<
    SearchMergePayload["context"]
  >({ search_merge_banners: [], search_merge_status: undefined });
  const fetchedRef = useRef<boolean>(false);
  const toast = useToast();

  const fragments = {
    context: true,
    [SEARCH_TABLE_ACCEPTED]: true,
    [SEARCH_TABLE_CONTACTED]: true,
    [SEARCH_TABLE_DECLINED]: true,
    [SEARCH_TABLE_FILTERED]: true,
    [SEARCH_TABLE_POTENTIAL]: true,
    [SEARCH_TABLE_REJECTED]: true,
    [SEARCH_TABLE_VALIDATED]: true,
  };

  const params = getSearchMergeParams({ pagination });

  const handleSearchMergeData = ({
    data,
  }: {
    data: {
      searchMergeRequests: SearchMergePayload;
    };
  }) => {
    if (!data?.searchMergeRequests) {
      return;
    }

    if (data.searchMergeRequests.context != null) {
      setSearchMergeContext(data.searchMergeRequests.context);
    }

    fillTableCaches({ auctionId, client, data, pagination });
  };

  const { data, error, loading, refetch } = useQuery<
    { searchMergeRequests: SearchMergePayload },
    { auctionId: number; queryParams: string }
  >(GET_SEARCH_MERGE_REQUESTS(fragments), {
    // network-only causes rerenders
    fetchPolicy: getFetchPolicy("no-cache"),
    skip: fetchedRef.current,
    variables: {
      auctionId,
      queryParams: params,
    },
  });

  useEffect(() => {
    if (data) handleSearchMergeData({ data });
  }, [data]);

  useEffect(() => {
    if (!error) return;
    console.error(
      `error fetching search merge results: ${getErrorMessage(error)}`,
    );
    toast({
      color: "danger",
      message: translations.auctionRequest.tryAgain,
    });
  }, [error]);

  const refreshData = async () => {
    try {
      fetchedRef.current = true;
      const refetched = await refetch({
        auctionId,
        queryParams: params,
      });
      handleSearchMergeData({ data: refetched.data });
    } catch (err) {
      console.error(
        `error refetching search merge results: ${getErrorMessage(err)}`,
        {
          err,
        },
      );
      toast({
        color: "danger",
        message: translations.auctionRequest.tryAgain,
      });
    }
  };

  useHandleAutoRefreshComponent(auctionId, refreshData);

  return [
    searchMergeContext,
    getQueryProgress(loading, error),
    refreshData,
  ] as const;
}

export const useGetSearchMergeTableTotals = ({
  auctionId,
}: {
  auctionId: number;
}) => {
  const { data, loading } = useQuery<
    { searchMergeTableTotals: SearchTableTotals & { __typename?: string } },
    { auctionId: number }
  >(GET_SEARCH_MERGE_TOTALS, {
    fetchPolicy: getFetchPolicy("cache-only"),
    variables: { auctionId },
  });

  return [loading, data?.searchMergeTableTotals] as const;
};

const getInitialTablePageState = ({
  storageKey,
}: {
  storageKey: string;
}): TablePageState => {
  const localStorageTablePageState = sessionStorage.getItem(storageKey);

  return localStorageTablePageState
    ? JSON.parse(localStorageTablePageState)
    : INITIAL_TABLE_PAGE_STATE;
};

export function useGetSearchMergeTable<
  TableData extends
    | AcceptedTablePayload
    | ContactedTablePayload
    | DeclinedTablePayload
    | FilteredTablePayload
    | PotentialReceiversTablePayload
    | RejectedTablePayload
    | ValidatedTablePayload,
>({ auctionId, table }: { auctionId: number; table: SearchTable }) {
  const storageKey = getSearchTableStorageKey({ auctionId, table });
  const shouldFetchFromCache = useRef<boolean>(true);
  const [currentTablePageState, setCurrentTablePageState] = useState(
    getInitialTablePageState({ storageKey }),
  );

  const paginationQueryString = getSearchMergeTableQuery({
    pageState: currentTablePageState,
    table,
  });

  const { data, error, loading } = useQuery<
    { searchMergeRequests: SearchMergePayload },
    { auctionId: number; queryParams: string }
  >(
    GET_SEARCH_MERGE_REQUESTS({
      [table]: true,
    }),
    {
      fetchPolicy: getFetchPolicy(
        shouldFetchFromCache.current ? "cache-only" : "cache-first",
      ),
      variables: {
        auctionId,
        queryParams: paginationQueryString,
      },
    },
  );

  const tablePayload = searchMergePayloadMapping[table];

  const tableData = data?.searchMergeRequests?.[tablePayload] as
    | TableData
    | undefined;

  useEffect(() => {
    if (!tableData?.total) return;
    const totalPages = Math.ceil(tableData.total / currentTablePageState.count);

    // Handle if the total number of pages is less that than available in the state
    if (currentTablePageState.page > totalPages) {
      const pageState: TablePageState = {
        count: currentTablePageState.count,
        page: 1,
        sortBy: currentTablePageState.sortBy,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(pageState));
      shouldFetchFromCache.current = false;
      setCurrentTablePageState(pageState);
    }
  }, [tableData?.total]);

  const handlePagination = useCallback(
    ({
      count,
      page,
      sortBy,
    }: {
      count: TableCount;
      page: number;
      sortBy: TableSortValue;
    }) => {
      shouldFetchFromCache.current = false;
      setCurrentTablePageState(() => {
        const pageState: TablePageState = {
          count,
          page,
          sortBy,
        };
        sessionStorage.setItem(storageKey, JSON.stringify(pageState));

        return pageState;
      });
    },
    [storageKey],
  );

  return {
    currentTablePageState,
    handlePagination,
    queryProgress: getQueryProgress(loading, error),
    tableData,
  };
}

export function useGetProductTour({
  name,
  skip,
}: {
  name: ProductTourKey;
  skip?: boolean;
}) {
  const { data, error, loading, refetch } = useQuery<
    { productTour: ProductTourCompletion },
    { name: ProductTourKey }
  >(GET_PRODUCT_TOUR, {
    fetchPolicy: "no-cache",
    skip,
    variables: { name },
  });
  const queryProgress = getQueryProgress(loading, error);
  const payload = data?.productTour;

  return [queryProgress, payload, { error, refetch }] as const;
}

export function useGetVersions({
  ...options
}: SafeQueryHookOptions<{ versions?: Version[] }> = {}) {
  const { data, error, loading, refetch, startPolling, stopPolling } =
    useQuery<{ versions?: Version[] }>(GET_VERSIONS, {
      ...options,
      fetchPolicy: getFetchPolicy(options.fetchPolicy),
    });

  usePolling({ interval: 60000, shouldPoll: true, startPolling, stopPolling });

  const versions = data?.versions;

  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, versions, { error, refetch }] as const;
}

export const useGetEncryptionJobsQuery = ({
  careseekerId,
  skip,
}: {
  careseekerId: number;
  skip?: boolean;
}) => {
  const { data, error, loading, refetch, startPolling, stopPolling } = useQuery<
    { encryptionJobs?: EncryptionJob[] | null },
    { careseekerId: number }
  >(GET_ENCRYPTION_JOBS, {
    skip,
    variables: { careseekerId },
  });
  usePolling({ shouldPoll: !skip, startPolling, stopPolling });

  return [
    getQueryProgress(loading, error),
    data?.encryptionJobs,
    { error, refetch },
  ] as const;
};

export function useGetProviderSearchPatients({ skip }: { skip: boolean }) {
  const { data, error, loading, refetch } = useQuery<{
    patients: ProviderSearchPatients;
  }>(GET_PROVIDER_SEARCH_PATIENTS, { skip });

  const patients = data?.patients;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, patients, { error, refetch }] as const;
}

const useGetProviderSearchDetailsQueryParams = ({
  filters,
}: {
  filters: ProviderSearchFilters;
}): string => {
  const queryParams: string[] = [];
  if (filters?.start_date) {
    queryParams.push(`${USER_FILTER_START_DATE}=${filters.start_date}`);
  }
  if (filters?.zipcode?.value) {
    queryParams.push(
      `${USER_FILTER_TREATMENT_LOCATION}=${filters.zipcode.value}`,
    );
  }

  return queryParams.join("&");
};

export function useGetProviderSearchConsultant({ id }: { id: number }) {
  const { filters } = useProviderSearchFilterContext();
  const queryParams = useGetProviderSearchDetailsQueryParams({ filters });

  const { data, error, loading, refetch } = useQuery<
    { consultant: ProviderSearchProvider },
    { id: number; queryParams: string }
  >(GET_PROVIDER_SEARCH_CONSULTANT, {
    skip: !filters.zipcode?.value,
    variables: { id, queryParams },
  });

  const consultant = data?.consultant;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, consultant, { error, refetch }] as const;
}

export function useGetProviderSearchCareprovider({ id }: { id: number }) {
  const { filters } = useProviderSearchFilterContext();
  const queryParams = useGetProviderSearchDetailsQueryParams({ filters });

  const { data, error, loading, refetch } = useQuery<
    { providerSearchProvider: ProviderSearchProvider },
    { id: number; queryParams: string }
  >(GET_PROVIDER_SEARCH_PROVIDER, {
    skip: !filters.zipcode?.value,
    variables: { id, queryParams },
  });

  const careprovider = data?.providerSearchProvider;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, careprovider, { error, refetch }] as const;
}

export function useGetProviderSearchOverview({
  fetchPolicy = "network-only",
  nextFetchPolicy,
  patientId,
}: {
  fetchPolicy?: WatchQueryFetchPolicy;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  patientId: number;
}) {
  const { data, error, loading, refetch } = useQuery<
    {
      providerSearchRequestOverview: ProviderSearchRequestsOverview;
    },
    { patientId: number }
  >(GET_PROVIDER_SEARCH_REQUEST_OVERVIEW, {
    fetchPolicy,
    nextFetchPolicy,
    variables: { patientId },
  });

  const requestOverview = data?.providerSearchRequestOverview;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, requestOverview, { error, refetch }] as const;
}

export function useLazyProviderSearchActivation() {
  const [activateProviderSearchAccount] = useLazyQuery<
    { activateProviderSearch: ProviderSearchActivation },
    { token: string }
  >(ACTIVATE_PROVIDER_SEARCH_ACCOUNT);

  return async (token: string) => {
    const { data } = await activateProviderSearchAccount({ token });
    return data?.activateProviderSearch;
  };
}

export function useLazyProviderSearchLoginActivation() {
  const [activateProviderSearchAccount] = useLazyQuery<
    { loginProviderSearch: ProviderSearchActivation },
    { token: string }
  >(LOGIN_ACTIVATION_PROVIDER_SEARCH_ACCOUNT);

  return async (token: string) => {
    const { data } = await activateProviderSearchAccount({ token });
    return data?.loginProviderSearch;
  };
}

export type LimitedAddress = Pick<
  Location,
  "city" | "latitude" | "longitude" | "zipcode" | "federal_state"
>;

export function useGetAddressFromZipcode({
  zipcode,
}: SafeQueryHookOptions<
  { address: LimitedAddress },
  { zipcode: string | undefined }
> & {
  zipcode: string | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<
    { address: LimitedAddress },
    { zipcode: string | undefined }
  >(GET_ADDRESS_FROM_ZIPCODE, {
    skip: !zipcode || zipcode?.length !== 5,
    variables: { zipcode },
  });

  useEffect(() => {
    if (error) {
      console.error(
        `error getting address from zipcode - ${getErrorMessage(error)}`,
      );
    }
  }, [error]);
  const queryProgress = getQueryProgress(loading, error);
  const address = data?.address;

  return [queryProgress, address, { error, refetch }] as const;
}

export function useLazyGetAddressFromZipcode() {
  const [getAddressFromZipcode] = useLazyQuery<
    { address: LimitedAddress },
    { zipcode?: string }
  >(GET_ADDRESS_FROM_ZIPCODE);
  return async (zipcode?: string): Promise<LimitedAddress | undefined> => {
    try {
      if (!zipcode || zipcode?.length !== 5) return;
      const { data } = await getAddressFromZipcode({ zipcode });
      return data?.address;
    } catch (err) {
      console.error(
        `error fetching address from zipcode: ${zipcode} - ${getErrorMessage(
          err,
        )}`,
      );
    }
  };
}

export function useZipcodeInCareproviderCatchment({
  careproviderId,
  zipcode,
}: {
  careproviderId: number;
  zipcode: string | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<
    { catchment: { inside_catchment: boolean } },
    { careproviderId: number; zipcode: string | undefined }
  >(ZIPCODE_INSIDE_CATCHMENT, {
    skip: !zipcode || zipcode?.length !== 5,
    variables: { careproviderId, zipcode },
  });
  const queryProgress = getQueryProgress(loading, error);
  const isInside = data?.catchment;

  return [queryProgress, isInside, { error, refetch }] as const;
}

export function useGetProviderSearchListing({
  filters,
}: {
  filters: ProviderSearchFilters;
}) {
  const queryParams = toFilterQueryString(filters);

  const { data, error, loading, refetch } = useQuery<
    { providerSearchListing: ProviderSearchListing },
    { queryParams: string }
  >(GET_PROVIDER_SEARCH_LISTING, {
    skip: !filters?.zipcode?.value,
    variables: { queryParams },
  });
  const providerSearchListing = data?.providerSearchListing;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, providerSearchListing, { error, refetch }] as const;
}

export function useGetConsultant({ id }: { id: number }) {
  const { data, error, loading, refetch } = useQuery<
    { consultant: ProviderSearchProvider },
    { id: number }
  >(GET_CONSULTANT, {
    skip: !id,
    variables: { id },
  });

  const consultant = data?.consultant;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, consultant, { error, refetch }] as const;
}

export function useGetConsultantEvents({
  count,
  id,
  page,
}: {
  count: number;
  id: number;
  page: number;
}) {
  const { data, error, loading, refetch } = useQuery<
    { eventPage: ConsultantEventPage },
    { id: number; queryParams: string }
  >(GET_CONSULTANT_EVENTS, {
    skip: !id,
    variables: {
      id,
      queryParams: `start=${count * (page - 1)}&count=${count}`,
    },
  });

  const consultantEvents = data?.eventPage;
  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, consultantEvents, { error, refetch }] as const;
}

export function useGetConsultantFileList({
  consultantId,
}: {
  consultantId: number;
}) {
  const { data, error, loading, refetch } = useQuery<
    { getFiles: Pick<CareproviderFiles, "images"> },
    { consultantId: number }
  >(GET_CONSULTANT_FILES, {
    variables: {
      consultantId,
    },
  });

  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, data?.getFiles, { error, refetch }] as const;
}

export function useGetEmailTranslations(options: SafeQueryHookOptions = {}) {
  const { data, error, loading, refetch } = useQuery<{
    translations: EmailTranslation[];
  }>(GET_EMAIL_TRANSLATIONS, options);

  const translations = data?.translations;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, translations, { error, refetch }] as const;
}

export function useGetEmailTranslation(
  id: string,
  options: SafeQueryHookOptions<
    { translation: EmailTranslation },
    { id: string }
  > = {},
) {
  const { data, error, loading, refetch } = useQuery<
    { translation: EmailTranslation },
    { id: string }
  >(GET_EMAIL_TRANSLATION, {
    variables: { id },
    ...options,
  });

  const translation = data?.translation;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, translation, { error, refetch }] as const;
}

export function useGetTransitionalCareRequests({
  auctionId,
}: {
  auctionId: number;
}) {
  const [lazyGetRequests, queryProgress] = useLazyQuery<{
    transitionalCareRequests: TransitionalCareImportedAuctionRequest[];
  }>(GET_TRANSITIONAL_CARE_REQUESTS, {
    fetchPolicy: "no-cache",
    variables: { auctionId },
  });

  const getRequests = async () => {
    try {
      const { data } = await lazyGetRequests();
      return data?.transitionalCareRequests;
    } catch (err) {
      console.error(
        `error importing transitional care requests - ${getErrorMessage(err)}`,
      );
      return null;
    }
  };

  return [queryProgress, getRequests] as const;
}

export function useGetDoctorForms({
  auctionId,
  options = {},
}: {
  auctionId: number | undefined;
  options?: SafeQueryHookOptions<GetDoctorFormsData, GetDoctorFormsVariables>;
}) {
  const careseekerId = useLoggedCareseeker()?.id;
  if (!careseekerId) {
    throw new Error("careseeker id is required");
  }

  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [progress, data, rest] = useGenericQuery<
    GetDoctorFormsData,
    GetDoctorFormsVariables
  >(GET_DOCTOR_FORMS, {
    context: { encryptionContext },
    variables: { auctionId, careseekerId },
    ...options,
    skip: !careseekerId || !auctionId,
  });

  return [progress, data?.auction, rest] as const;
}

export function useGetChats(options?: SafeQueryHookOptions<GetChatsData>) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [progress, data, rest] = useGenericQuery<GetChatsData>(GET_CHATS, {
    ...options,
    context: { encryptionContext },
  });

  return [
    isLoading(progress),
    {
      chats: data?.chatsPage?.chats ?? [],
      has_more: data?.chatsPage?.has_more ?? false,
      next_cursor: data?.chatsPage?.next_cursor ?? null,
    },
    rest,
  ] as const;
}

export function useGetChat({
  chatId,
  ...options
}: {
  chatId: number | undefined;
} & SafeQueryHookOptions<GetChatData, GetChatVariables>) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [progress, data, rest] = useGenericQuery<GetChatData, GetChatVariables>(
    GET_CHAT,
    {
      ...options,
      context: { encryptionContext },
      skip: !chatId || options.skip,
      variables: { id: chatId },
    },
  );

  return [isLoading(progress), data?.chat, rest] as const;
}

export function useGetBedrockPlaygroundData(props?: {
  options?: SafeQueryHookOptions<{
    bedrockPlayground: BedrockPlaygroundPage;
  }>;
}) {
  const { data, error, loading, refetch } = useQuery<{
    bedrockPlayground: BedrockPlaygroundPage;
  }>(GET_BEDROCK_PLAYGROUND, props?.options);

  const bedrockPlayground = data?.bedrockPlayground;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, bedrockPlayground, { error, refetch }] as const;
}

export function useSealdGroupSessionsCheck({
  sealdId,
}: {
  sealdId: string | undefined;
}) {
  return useGenericQuery<
    { sealdGroupSessions: GroupSessionsResponse },
    { sealdId: string | undefined }
  >(SEALD_GROUP_SESSIONS_CHECK, {
    fetchPolicy: "network-only",
    variables: { sealdId },
  });
}

export function useGetSocialWorkerAccounts({
  careseekerId,
}: {
  careseekerId: number | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<{
    socialWorkers: Readonly<Account[]>;
  }>(GET_SOCIAL_WORKER_ACCOUNTS, {
    skip: careseekerId == null,
    variables: { careseekerId },
  });

  const socialWorkers = data?.socialWorkers;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, socialWorkers, { error, refetch }] as const;
}

export function useGetPredictSocialWorkerAccounts({
  careseekerId,
}: {
  careseekerId: number | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<{
    predictSocialWorkers: Readonly<Account[]>;
  }>(GET_PREDICT_SOCIAL_WORKER_ACCOUNTS, {
    skip: !careseekerId,
    variables: { careseekerId },
  });

  const socialWorkers = data?.predictSocialWorkers;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, socialWorkers, { error, refetch }] as const;
}

export function useGetAvailableSearchTypes({
  patientId,
}: {
  patientId: number;
}) {
  const { data, error, loading, refetch } = useQuery<
    { getAvailableSearchTypes: Readonly<AvailableSearchTypes> },
    { patientId: number }
  >(GET_AVAILABLE_SEARCH_TYPES, {
    fetchPolicy: "network-only",
    variables: { patientId: patientId },
  });

  const payload = data?.getAvailableSearchTypes?.search_types || [];
  const queryProgress = getQueryProgress(loading, error);

  return [payload, queryProgress, { error, refetch }] as const;
}

export function useGetOnpremiseLogs({
  careseekerId,
  configId,
}: {
  careseekerId: number | undefined;
  configId: number | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<
    { getOnpremiseLogs: { message: string; timestamp: string }[] },
    { careseekerId: number; configId: number }
  >(GET_ONPREMISE_LOGS, {
    skip: !configId || !careseekerId,
    variables: { careseekerId: careseekerId ?? -1, configId: configId ?? -1 },
  });

  const queryProgress = getQueryProgress(loading, error);

  return [queryProgress, data?.getOnpremiseLogs, { error, refetch }] as const;
}

export function useGetStationsForAccountRole({
  account_id,
  careseeker_id,
  options,
  role_value,
}: {
  account_id?: number;
  careseeker_id?: number;
  options?: SafeQueryHookOptions;
  role_value: number;
}) {
  const { data, error, loading, refetch } = useQuery(
    GET_STATIONS_FOR_ACCOUNT_ROLE,
    {
      variables: {
        account_id,
        careseeker_id,
        role_value,
      },
      ...options,
    },
  );

  const queryProgress = getQueryProgress(loading, error);
  const stations = data?.stationsForAccountRole || [];

  return [stations, queryProgress, { data, error, loading, refetch }] as const;
}

export function useGetNativeTranslations(options: SafeQueryHookOptions = {}) {
  const { data, error, loading, refetch } = useQuery<{
    nativeTranslations: TranslationType[];
  }>(GET_NATIVE_TRANSLATIONS, options);

  const translations = data?.nativeTranslations;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, translations, { error, refetch }] as const;
}

export function useGetNativeTranslation(
  id: string,
  options: SafeQueryHookOptions<
    { nativeTranslation: TranslationType },
    { id: string }
  > = {},
) {
  const { data, error, loading, refetch } = useQuery<
    { nativeTranslation: TranslationType },
    { id: string }
  >(GET_NATIVE_TRANSLATION, {
    variables: { id },
    ...options,
  });

  const translation = data?.nativeTranslation;
  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, translation, { error, refetch }] as const;
}

const useUpdateUrlForRecommendations = (requestedRequestID: number) => {
  const navigate = useNavigate();
  const client = useApolloClient();

  return ({
    auctionRequest,
  }: {
    auctionRequest: AuctionRequest | undefined;
  }) => {
    if (!auctionRequest?.id || auctionRequest.id === requestedRequestID) return;

    client.writeQuery({
      data: {
        auctionRequest,
      },
      query: GET_AUCTIONREQUEST,
      variables: {
        id: auctionRequest.id,
        ref: getReferrer(),
      },
    });

    navigate(
      {
        pathname: window.location.pathname.replace(
          `/${requestedRequestID}`,
          `/${auctionRequest.id}`,
        ),
        search: location.search,
      },
      { replace: true },
    );
  };
};

export const useGetAuctionRequest = ({
  auctionRequestId,
  fetchPolicy,
  ...options
}: SafeQueryHookOptions<
  { auctionRequest: AuctionRequest },
  { id: number; ref: string }
> & {
  auctionRequestId: number;
  fetchPolicy?: WatchQueryFetchPolicy;
}) => {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const updateUrl = useUpdateUrlForRecommendations(auctionRequestId);
  const policy = useGetAuctionRequestFetchPolicy({ fetchPolicy });

  const [queryProgress, data, { error, refetch }] = useGenericQuery<
    { auctionRequest: AuctionRequest },
    { id: number; ref: string }
  >(GET_AUCTIONREQUEST, {
    context: { encryptionContext },
    fetchPolicy: policy,
    skip: auctionRequestId <= 0,
    variables: {
      id: auctionRequestId,
      ref: getReferrer(),
    },
    ...options,
  });

  const auctionRequest = data?.auctionRequest;
  useEffect(() => {
    if (auctionRequest) updateUrl({ auctionRequest });
     
  }, [auctionRequest]);

  return [queryProgress, auctionRequest, { error, refetch }] as const;
};

export function useLazyGetSSOIntegrationRights() {
  const careseeker = useLoggedCareseeker();
  const [fetchSsoIntegrationRights] = useLazyQuery<
    { getSsoIntegrationRights: SSOIntegrationsRight[] },
    { domain: string; id?: number }
  >(GET_SSO_INTEGRATION_RIGHTS, {
    variables: { domain: "", id: careseeker?.id },
  });

  const getSSOIntegrationsRight = useCallback(
    async (...args: Parameters<typeof fetchSsoIntegrationRights>) => {
      const { data } = await fetchSsoIntegrationRights(...args);

      return data?.getSsoIntegrationRights;
    },
    [fetchSsoIntegrationRights],
  );

  return [getSSOIntegrationsRight];
}

export function useGetSSOIntegrationImport(
  careseeker_id: number | undefined,
  skip?: boolean,
) {
  return useQuery<
    { getSsoIntegrationImport: SSOIntegration },
    { careseeker_id: number }
  >(GET_SSO_INTEGRATION_IMPORT, {
    fetchPolicy: "network-only",
    skip: !careseeker_id || skip,
    variables: { careseeker_id: careseeker_id! },
  });
}

export function useLazyGetReceiverDocsApiPublicKeys() {
  const [getReceiverDocsApiPublicKeys] = useLazyQuery<
    { receiverDocsApiPublicKeys: ReceiverDocsApiPublicKeys },
    { auctionId: number }
  >(GET_RECEIVER_DOCS_API_PUBLIC_KEYS);
  return async (auctionId: number) => {
    const { data } = await getReceiverDocsApiPublicKeys({ auctionId });
    return data?.receiverDocsApiPublicKeys ?? [];
  };
}

export function useLazyGetEmbedSession({
  careproviderId,
  fetchPolicy,
}: {
  careproviderId: number;
  fetchPolicy?: WatchQueryFetchPolicy;
}) {
  const [getEmbedSession] = useLazyQuery<
    {
      acquireEmbedSession: EmbedCookielessSessionAcquireResponse;
    },
    { id: number }
  >(GET_LOOKER_EMBED_SESSION, {
    fetchPolicy,
    variables: { id: careproviderId },
  });

  return useCallback(async () => {
    const { data } = await getEmbedSession();

    return [data?.acquireEmbedSession] as const;
  }, [getEmbedSession]);
}

export function useGetCareProviderAdmissionsPage({
  admissionsId,
}: {
  admissionsId?: string | undefined;
}) {
  const { data, error, loading, refetch } = useQuery<
    { getAdmissionsPage: CareproviderAdmissionPage },
    { admissionsId: string }
  >(GET_CAREPROVIDER_ADMISSIONS_PAGE, {
    skip: !admissionsId,
    variables: {
      admissionsId: admissionsId || "",
    },
  });

  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, data?.getAdmissionsPage, { error, refetch }] as const;
}

export function useLazyAdmissionsActivation() {
  const [activateAdmissions] = useLazyQuery<
    { loginAdmissions: AdmissionsActivation },
    { admissionsId: string; token: string }
  >(ADMISSIONS_LOGIN_WIZARD);

  return async ({
    admissionsId,
    token,
  }: {
    admissionsId: string;
    token: string;
  }) => {
    const { data } = await activateAdmissions({ admissionsId, token });
    return data?.loginAdmissions;
  };
}

export function useGetAdmissionsPatient({
  admissionsId,
}: {
  admissionsId: string | undefined;
}) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });

  const [progress, data, rest] = useGenericQuery<
    { auction: Auction },
    { admissionsId: string }
  >(GET_ADMISSIONS_AUCTION, {
    context: { encryptionContext },
    fetchPolicy: "network-only",
    skip: !admissionsId,
    variables: {
      admissionsId: admissionsId ?? "",
    },
  });

  return [progress, data?.auction, rest] as const;
}

export function useGetAuctionForms({
  auctionId,
  ...options
}: {
  auctionId: number;
} & SafeQueryHookOptions<
  { auction: { forms?: AuctionForm[] } },
  { auctionId: number }
>) {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const { data, error, loading, refetch } = useQuery<
    { auction: { forms?: AuctionForm[] } },
    { auctionId: number }
  >(GET_AUCTION_FORMS, {
    context: { encryptionContext },
    fetchPolicy: "network-only",
    variables: { auctionId },
    ...options,
  });

  const forms = data?.auction?.forms;

  const queryProgress = getQueryProgress(loading, error);
  return [queryProgress, forms, { error, refetch }] as const;
}

export function useLazyGetAuctionForms() {
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });
  const [getAuctionForms] = useLazyQuery<
    { auction: { forms?: AuctionForm[] } },
    { auctionId: number }
  >(GET_AUCTION_FORMS, { encryptionContext });

  return async ({
    auctionId,
  }: {
    auctionId: number;
  }): Promise<AuctionForm[] | undefined> => {
    try {
      const { data } = await getAuctionForms({ auctionId });
      return data?.auction?.forms;
    } catch (err) {
      console.error(`error fetching auction forms - ${getErrorMessage(err)}`);
      return undefined;
    }
  };
}
